import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import { Check, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import PublicNav from "@/components/PublicNav";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PLANS = [
    {
        name: "Free",
        price: 0,
        tag: "Try it",
        lookup_key: null,
        features: [
            "5 repurposes / month",
            "Instagram + TikTok captions",
            "YouTube link → posts",
            "History archive",
        ],
        cta: "Start free",
        testid: "pricing-free",
    },
    {
        name: "Starter",
        price: 9,
        tag: "7-day free trial",
        highlight: true,
        lookup_key: "starter_monthly",
        plan_id: "starter",
        features: [
            "100 repurposes / month",
            "Instagram + TikTok captions",
            "YouTube link → posts",
            "Priority generation",
            "History archive",
        ],
        cta: "Start free trial",
        testid: "pricing-starter",
    },
    {
        name: "Pro",
        price: 29,
        tag: "For creators",
        lookup_key: "pro_monthly",
        plan_id: "pro",
        features: [
            "500 repurposes / month",
            "Instagram + TikTok captions",
            "1 LinkedIn thread per repurpose",
            "5 Shorts/Reels script ideas",
            "1 Newsletter version",
            "All 5 platforms unlocked",
            "7-day free trial",
        ],
        cta: "Start free trial",
        testid: "pricing-pro",
    },
];

export default function Pricing() {
    const { user } = useAuth();
    const nav = useNavigate();
    const [loadingPkg, setLoadingPkg] = useState(null);

    const checkout = async (lookup_key, plan_id) => {
        if (!user) {
            nav("/register");
            return;
        }
        if (!lookup_key) {
            nav("/app/dashboard");
            return;
        }
        setLoadingPkg(lookup_key);
        try {
            const { data } = await api.post("/checkout/session", {
                lookup_key,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Checkout failed");
            setLoadingPkg(null);
        }
    };

    const openPortal = async () => {
        try {
            const { data } = await api.post("/billing/portal");
            window.location.href = data.url;
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Could not open billing portal");
        }
    };

    return (
        <div className="min-h-screen bg-[#050505] text-white">
            <PublicNav />
            <section className="pt-32 pb-24 px-6 sm:px-10">
                <div className="max-w-6xl mx-auto">
                    <div className="font-mono-ui text-xs tracking-[0.25em] uppercase text-zinc-500 mb-3">// PRICING</div>
                    <h1 className="font-display font-black text-5xl sm:text-7xl tracking-tighter max-w-3xl">
                        Pay for output.<br />
                        <span className="text-zinc-500">Not features.</span>
                    </h1>
                    <p className="mt-6 text-zinc-400 max-w-xl text-lg">
                        Simple monthly plans. Cancel anytime. Powered by GPT-5.2.
                    </p>

                    <div className="mt-16 grid md:grid-cols-3 gap-px bg-white/10 border border-white/10">
                        {PLANS.map((p) => {
                            const isCurrent = user && user.plan === (p.plan_id || "free");
                            return (
                                <div
                                    key={p.name}
                                    data-testid={p.testid}
                                    className={`p-8 ${p.highlight ? "bg-white text-black" : "bg-[#050505]"} flex flex-col`}
                                >
                                    <div className={`font-mono-ui text-[10px] tracking-[0.25em] uppercase ${p.highlight ? "text-black/60" : "text-zinc-500"}`}>
                                        {p.tag}
                                    </div>
                                    <div className="mt-4 font-display font-black text-2xl tracking-tight">{p.name}</div>
                                    <div className="mt-6 flex items-baseline gap-2">
                                        <span className="font-display font-black text-6xl tracking-tighter">€{p.price}</span>
                                        <span className={`text-base ${p.highlight ? "text-black/60" : "text-zinc-500"}`}>/mo</span>
                                    </div>
                                    <ul className={`mt-8 space-y-3 text-sm flex-1 ${p.highlight ? "text-black/80" : "text-zinc-400"}`}>
                                        {p.features.map((f) => (
                                            <li key={f} className="flex items-start gap-2">
                                                <Check className="w-4 h-4 mt-0.5 shrink-0" strokeWidth={2} />
                                                <span>{f}</span>
                                            </li>
                                        ))}
                                    </ul>
                                    {isCurrent && user?.has_stripe_customer ? (
                                        <Button
                                            data-testid={`${p.testid}-manage`}
                                            onClick={openPortal}
                                            className={`mt-8 w-full h-11 font-semibold ${
                                                p.highlight
                                                    ? "bg-black text-white hover:bg-zinc-800"
                                                    : "bg-white text-black hover:bg-zinc-200"
                                            }`}
                                        >
                                            Manage subscription →
                                        </Button>
                                    ) : (
                                        <Button
                                            data-testid={`${p.testid}-cta`}
                                            onClick={() => checkout(p.lookup_key, p.plan_id)}
                                            disabled={isCurrent || loadingPkg === p.lookup_key}
                                            className={`mt-8 w-full h-11 font-semibold ${
                                                p.highlight
                                                    ? "bg-black text-white hover:bg-zinc-800"
                                                    : "bg-white text-black hover:bg-zinc-200"
                                            }`}
                                        >
                                            {loadingPkg === p.lookup_key ? (
                                                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Redirecting…</>
                                            ) : isCurrent ? "Current plan" : `${p.cta} →`}
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                    </div>

                    <p className="mt-10 text-zinc-500 text-sm font-mono-ui uppercase tracking-widest">
                        // 7-day free trial. Cancel anytime from the billing portal. Test mode — use card 4242 4242 4242 4242.
                    </p>
                </div>
            </section>
        </div>
    );
}
